import { useEffect, useMemo, useRef } from "react"
import { Grid } from "@mui/material"
import { debounce, isEqual } from "lodash-es"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { DynamicConfigKey } from "../form/formTypes"
import type { MeetingServiceConfig, MainOrderFormikRef, MeetingServiceTabValues } from "../form/formTypes"
import { CreateOrderDialogMode, MeetingServicePriceListState, SelectedMainUserState } from "../../createOrderTypes"
import { DRAWER_WIDTH, DrawerContent } from "../drawer/DrawerContent"
import { ShopList } from "../shop/ShopList"

const useStyles = makeStyles()(() => ({
    contentSlide: {
        height: "100%",
        display: "grid",
        gridTemplateColumns: `1fr ${DRAWER_WIDTH}`,
    },
}))

type MeetingServiceFormContentProps = Readonly<{
    user: SelectedMainUserState
    handleClose: () => void
    handleOpenSelectPriceListsDialog: () => void
    meetingServiceConfiguration?: MeetingServiceConfig
    outerFormik: MainOrderFormikRef
    mode: CreateOrderDialogMode
    orderId?: number
}>

export function MeetingServiceFormContent({
    user,
    handleClose,
    handleOpenSelectPriceListsDialog,
    meetingServiceConfiguration,
    outerFormik,
    mode,
    orderId,
}: MeetingServiceFormContentProps) {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<MeetingServiceTabValues>()
    const priceListState: MeetingServicePriceListState = values["priceList"]
    const { item: priceListItem } = priceListState

    const debouncedSyncValues = useMemo(
        () =>
            debounce((newValues: MeetingServiceTabValues) => {
                Object.entries(newValues).forEach(([key, value]) => {
                    const outerValue = (outerFormik?.current?.values as Record<string, unknown> | undefined)?.[key]
                    // Avoid writing the same value again (can cause reinitialize flicker and query re-fetch loops).
                    if (isEqual(outerValue, value)) return

                    outerFormik?.current?.setFieldValue(key, value)
                })
            }, 400),
        [outerFormik],
    )

    useEffect(() => {
        const outerPriceList = outerFormik.current?.values["priceList"]
        const outerId = outerPriceList?.item?.priceListId
        const localId = priceListItem?.priceListId

        if (outerId && outerId !== localId) {
            setFieldValue("priceList", outerPriceList)
        }
    }, [outerFormik.current?.values["priceList"], priceListItem?.priceListId])

    useEffect(() => {
        debouncedSyncValues(values)
    }, [values, debouncedSyncValues])

    // Flush the debounced sync immediately when multipleDeliveries changes
    // (e.g. stale extra-service removal) so the outer Formik / Order Summary cart stays in sync.
    const deliveries = values[DynamicConfigKey.MULTIPLE_DELIVERIES]
    const prevDeliveriesRef = useRef(deliveries)

    useEffect(() => {
        if (isEqual(prevDeliveriesRef.current, deliveries)) return
        prevDeliveriesRef.current = deliveries
        debouncedSyncValues.flush()
    }, [deliveries, debouncedSyncValues])

    useEffect(() => {
        return () => {
            debouncedSyncValues.cancel()
        }
    }, [debouncedSyncValues])

    return (
        <Grid container className={classes.contentSlide}>
            {priceListItem && (
                <ShopList
                    meetingServiceConfiguration={meetingServiceConfiguration}
                    handleCloseMainDialog={handleClose}
                    handleOpenSelectPriceListsDialog={handleOpenSelectPriceListsDialog}
                    user={user}
                    orderId={orderId}
                    mode={mode}
                />
            )}
            {meetingServiceConfiguration && (
                <DrawerContent
                    meetingServiceConfiguration={meetingServiceConfiguration}
                    user={user}
                    mode={mode}
                    merchantId={priceListItem?.merchantId}
                />
            )}
        </Grid>
    )
}
